import { ref } from "vue";
import * as powerbi from "powerbi-client";
import type { IEmbedConfiguration, Report } from "powerbi-client";
import type { ReportEmbedToken } from "@/types/reports";

// Encapsulates powerbi-client embedding given a pre-fetched embed token.
// The token is fetched by the caller so it can decide whether to show the
// report at all (a failed token must not open an empty viewer).
// Reused by the viewer modal and the standalone pop-out page.
export function usePowerBiEmbed() {
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);

  const service = new powerbi.service.Service(
    powerbi.factories.hpmFactory,
    powerbi.factories.wpmpFactory,
    powerbi.factories.routerFactory
  );

  let embeddedReport: Report | null = null;
  let container: HTMLElement | null = null;

  function embed(target: HTMLElement, token: ReportEmbedToken): void {
    isLoading.value = true;
    error.value = null;
    container = target;

    const config: IEmbedConfiguration = {
      type: "report",
      id: token.reportId,
      embedUrl: token.embedUrl,
      accessToken: token.token,
      tokenType: powerbi.models.TokenType.Embed,
      permissions: powerbi.models.Permissions.Read,
      settings: {
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: true },
        },
        bars: {
          actionBar: { visible: true },
        },
        layoutType: powerbi.models.LayoutType.Custom,
        customLayout: {
          displayOption: powerbi.models.DisplayOption.FitToWidth,
        },
      },
    };

    service.reset(target);
    embeddedReport = service.embed(target, config) as Report;

    // Paginated (RDL) reports don't emit the "loaded" event, so relying on it
    // would leave our spinner up forever. Detect them by the rdlEmbed URL and
    // clear loading now; the paginated viewer shows its own loading indicator.
    const isPaginated = token.embedUrl?.toLowerCase().includes("rdlembed") ?? false;
    if (isPaginated) {
      isLoading.value = false;
    }

    embeddedReport.on("loaded", () => {
      isLoading.value = false;
    });

    embeddedReport.on("error", () => {
      error.value = "The report could not be displayed.";
      isLoading.value = false;
    });
  }

  function reset(): void {
    if (container) {
      service.reset(container);
    }
    embeddedReport = null;
    container = null;
  }

  return { isLoading, error, embed, reset };
}
